/**
 * priceAdapter — single place to change pricing source.
 *
 * Currently wired to CardMarket data embedded in the card document.
 * To switch to a different API (TCGPlayer, PriceCharting, etc.):
 *   1. Import (or fetch) from the new source.
 *   2. Replace the body of `getRawPrice` below.
 *   3. Nothing else in the codebase needs to change.
 *
 * All pricing logic in the app imports from here, NOT from individual
 * components, so a single edit swaps the source everywhere.
 */

// Grade scale, multipliers and legacy normalization live in ONE place:
import {
  CONDITION_MULTIPLIERS,
  normalizeCondition,
  conditionMultiplier,
  DEFAULT_CONDITION,
} from "./conditionScale";
// The one place that defines what currency stored figures are in, and the one
// list of codes it can actually render. Imported so this module cannot drift
// from the formatting boundary that renders them — see the binding below.
import { SOURCE_CURRENCY, SUPPORTED_DISPLAY_CURRENCIES } from "./currency";

export { CONDITION_MULTIPLIERS };

/**
 * Currencies a provider-quoted `conditionPrices` row may be RENDERED in.
 *
 * This is the whole revert switch for showing dollars. Set it to ["EUR"] and
 * the app's behaviour returns exactly to what it was — one line, no data
 * change, no call-site change, and every mixed-currency state below becomes
 * unreachable again.
 *
 * A code not on this list is refused rather than converted: there is no
 * approved FX source (utils/currency.js getActiveRates), so a figure we cannot
 * express is a figure we cannot show.
 *
 * Editing it in the OTHER direction is bounded by the binding below: a code
 * added here must be one utils/currency.js can format, or the module refuses
 * to load. Frozen because it is a switch, not a collection — nothing in the
 * app has any business pushing onto it at runtime.
 */
export const ALLOWED_PRICE_CURRENCIES = Object.freeze(["USD", "EUR"]);

/**
 * Codes on `allowed` that the formatting boundary cannot render.
 *
 * Exported so the guard below can be SHOWN to reject something. A check whose
 * failure branch nothing ever exercises is a comment with a throw in it: the
 * one call that matters passes the real constant and is expected to return
 * empty forever, which proves nothing about what happens when it does not.
 *
 * @param {string[]} [allowed]
 * @returns {string[]}
 */
export const unrenderablePriceCurrencies = (allowed = ALLOWED_PRICE_CURRENCIES) =>
  (allowed || []).filter((code) => !SUPPORTED_DISPLAY_CURRENCIES.includes(code));

// THE BINDING. The docstring above calls ALLOWED_PRICE_CURRENCIES "the whole
// revert switch", and it is — in the direction that removes a currency. Adding
// one was never wired to anything: utils/currency.js formatPrice DISCARDS an
// amountCurrency it does not support and prints the figure with the source
// symbol instead, so allowing "GBP" here would not fail anywhere. It would put
// a pound figure on screen behind a euro sign — the #64 bug the currency module
// exists to prevent, re-entering through the one list nobody thought to check.
//
// Asserted at module load rather than at the call site because a call-site
// check can be reached by no test, on no route, until a collector reaches it
// first. Every screen in the app imports this module, so an unsupported code
// takes the build down in the first render and the first test run — loudly, at
// the moment someone edits the switch, which is the only moment the mistake is
// cheap to fix.
const UNRENDERABLE_PRICE_CURRENCIES = unrenderablePriceCurrencies();
if (UNRENDERABLE_PRICE_CURRENCIES.length > 0) {
  throw new Error(
    `ALLOWED_PRICE_CURRENCIES contains ${UNRENDERABLE_PRICE_CURRENCIES.join(", ")}, ` +
      `which utils/currency.js cannot render (it formats ` +
      `${SUPPORTED_DISPLAY_CURRENCIES.join(", ")} and relabels anything else as ` +
      `${SOURCE_CURRENCY}). Add the code to SUPPORTED_DISPLAY_CURRENCIES there, ` +
      "or drop it here: a price we cannot format is a price we must not show."
  );
}

/**
 * THE INVARIANT, restated for this module: an amount never travels without its
 * currency. Every getter below returns `{value|price, currency}` and the only
 * place two numbers are added is `partitionByCurrency`, which adds them only
 * when their codes are identical. A `currency` of null means "no price", never
 * "assume euros".
 */

// ── adapter implementation ──────────────────────────────────────────────────

/**
 * Returns true only when the card has at least one usable market price > 0.
 * 0 is never treated as a known price — it signals missing data.
 *
 * This must stay in step with `getRawPrice` below: it reads the same Cardmarket
 * fields and nothing else. It used to carry a TCGplayer fallback that
 * `getRawPrice` had already dropped, so a card with a tcgplayer block and no
 * cardmarket block was called "priced" while the getter returned 0 — the page
 * header counted it, added 0.00 for it, and left it out of the "· N unpriced"
 * note, all while the tile beneath it printed "No estimate".
 *
 * The branch is gone rather than mirrored into `getRawPrice`, because TCGplayer
 * quotes USD into a field the whole app renders as SOURCE_CURRENCY and there is
 * no approved FX source (see the note on getRawPrice). For such a card the app
 * has no price it can express, and reporting one is the lie.
 *
 * NARROW BY DESIGN: this answers "does Cardmarket price this card", not "can we
 * price this card". Use `hasAnyPrice` for the second question — a card the
 * provider feed prices and Cardmarket does not returns false here.
 *
 * @param {object} card — populated card document or API card object
 * @returns {boolean}
 */
export const hasRawPrice = (card) => {
  if (!card) return false;
  const cm = card?.cardmarket?.prices;
  if (cm) {
    return !!(
      cm.averageSellPrice > 0 ||
      cm.avg30 > 0 ||
      cm.avg7 > 0 ||
      cm.avg1 > 0 ||
      cm.trendPrice > 0
    );
  }
  return false;
};

/**
 * Returns true when a collection entry (UserCard) has a usable price.
 * Checks askingPrice first (user-set), then falls back to raw card market data.
 * @param {object} entry — UserCard document
 * @returns {boolean}
 */
export const hasTradeSidePrice = (entry, conditionOverride) => {
  if (!entry) return false;
  const ap = entry.askingPrice;
  if (typeof ap === "number" && !Number.isNaN(ap) && ap > 0) return true;
  const raw = entry.cardId;
  const card =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  // hasAnyPrice, not hasRawPrice: a card priced only by the provider feed has
  // no cardmarket block at all, so the Cardmarket-only predicate would call it
  // unpriced while getTradeSideValue handed back a real figure — #185's
  // predicate/getter split, re-opened by the first USD-only card.
  return hasAnyPrice(card || entry, conditionOverride || entry.condition);
};

/**
 * Returns true when a persisted trade card subdoc has a reliable price.
 * priceSnapshot of 0 is treated as unknown — existing trades may have been
 * saved with the pre-fix bug that coerced null → 0.
 * Falls back to populated card data when snapshot is missing.
 * @param {object} c — trade card subdoc (offeredCards[n] or requestedCards[n])
 * @returns {boolean}
 */
export const hasSnapshotPrice = (c) => {
  if (!c) return false;
  const snap = c.priceSnapshot;
  if (typeof snap === "number" && !Number.isNaN(snap) && snap > 0) return true;
  const card = c.cardId && typeof c.cardId === "object" ? c.cardId : null;
  return hasAnyPrice(card || {}, c.condition);
};

/**
 * Extract the best available raw market price from a card object.
 *
 * @returns {{value: number, currency: string|null}} — `{value: 0, currency:
 *   null}` when unknown. The tag is SOURCE_CURRENCY because this branch reads
 *   Cardmarket, and Cardmarket quotes EUR. It is returned rather than assumed
 *   by the caller so a future second raw source cannot be silently relabelled.
 *
 * The annotation used to say USD, which was wrong in a dangerous direction: the
 * primary branch returns Cardmarket data, and Cardmarket quotes EUR — which IS
 * the app's source currency. Anyone trusting the old note and adding a USD→EUR
 * conversion would have corrupted every price in the app.
 */
export const getRawPrice = (card) => {
  if (!card) return { value: 0, currency: null };

  // Current source: CardMarket prices embedded in the card doc.
  //
  // The `> 0` gate is the same one `hasRawPrice` applies, and it is here rather
  // than only there on purpose: this used to be a bare `||` chain, and -5 is
  // truthy. For {averageSellPrice: -5, avg30: 10} the predicate answered "priced"
  // off avg30 while this returned -5, so summariseEstimatedValue counted the card
  // and SUBTRACTED from a page header total. A negative provider figure is not a
  // price; it is missing data, exactly as 0 is.
  //
  // The FIRST positive field wins. Keep it that way: this is a preference order,
  // not a search for the biggest number.
  const cm = card?.cardmarket?.prices;
  if (cm) {
    const preferred = [
      cm.averageSellPrice,
      cm.avg30,
      cm.avg7,
      cm.avg1,
      cm.trendPrice,
    ];
    // `find` yields undefined when nothing qualifies; `|| 0` keeps the documented
    // "0 if unknown" return, which callers multiply by a condition multiplier.
    const value = preferred.find((v) => v > 0) || 0;
    return value > 0
      ? { value, currency: SOURCE_CURRENCY }
      : { value: 0, currency: null };
  }

  // No TCGplayer fallback. It was labelled "future — already handles if we start
  // embedding tcgplayer data", but the data is embedded today (fullSync spreads
  // the whole API card and the schema defines a tcgplayer block), so that branch
  // was live — returning a USD figure that every caller then rendered with a
  // euro sign. A price we cannot express in the source currency is not a price
  // we can show, and there is no approved FX source to convert it (see
  // utils/currency.js getActiveRates, which deliberately returns null).
  //
  // Still no tcgplayer branch, and the reason is now the DATA as much as the
  // currency: measured in server/initialdata/initialPkemonTCGdata.json, a
  // tcgplayer block with a positive `market` exists on 15 of 102 cards, in the
  // `holofoil` variant only, stamped 2023/07/09. Sparse, stale, and no per-copy
  // variant is recorded — so we could not say which of those prices applies to
  // the card in someone's hand.
  return { value: 0, currency: null };
};

// ── condition-based pricing ──────────────────────────────────────────────────

/**
 * Best available VERIFIED per-condition sale price for a card, if the feed
 * carries one. Real sales feeds (TCGplayer / eBay sold) report prices per
 * condition tier; when we embed that data it lives on `card.conditionPrices`
 * keyed by canonical grade. Until a feed is wired, this returns null and we
 * fall back to a multiplier estimate.
 * @returns {{value: number, currency: string}|null}
 */
const getVerifiedConditionPrice = (card, condition) => {
  const cp = card?.conditionPrices;
  if (!cp) return null;

  // These come from the price provider, which quotes in its own currency
  // (JustTCG: USD). This used to demand SOURCE_CURRENCY and so discarded every
  // dollar the paid feed fetched, falling back to a Cardmarket estimate. The
  // figure is now carried WITH its code and rendered in it, which is what makes
  // a dollar showable at all; ALLOWED_PRICE_CURRENCIES is the switch.
  //
  // "Rendered in it" is a guarantee only because of the binding above. It was
  // true of USD and EUR by luck of both happening to be in the formatter's
  // SUPPORTED set; for any third code it would have been false, and the tag
  // this getter so carefully preserves would have been thrown away one layer
  // down and printed as a euro.
  //
  // An UNTAGGED row still returns null. Rows written before `currency` existed
  // are USD by history, and an absent tag is not a claim — refreshConditionPrices
  // writes the tag on every row it persists, so nothing current is lost.
  const tag =
    typeof cp.currency === "string" ? cp.currency.trim().toUpperCase() : "";
  if (!ALLOWED_PRICE_CURRENCIES.includes(tag)) return null;

  const val = cp[normalizeCondition(condition)];
  return typeof val === "number" && val > 0 ? { value: val, currency: tag } : null;
};

/**
 * Condition-adjusted price WITH provenance, so the UI can be honest about
 * whether a number is a real sale or an estimate — and about which currency it
 * is quoting.
 *
 * Preference order is unchanged: a verified per-condition figure wins when it
 * is present and tagged, otherwise the Cardmarket multiplier estimate.
 * @returns {{ price: number, currency: string|null,
 *             source: 'sales'|'estimate', isEstimate: boolean }}
 */
export const getConditionPrice = (card, condition) => {
  const cond = condition || DEFAULT_CONDITION;

  const verified = getVerifiedConditionPrice(card, cond);
  if (verified != null) {
    return {
      price: +verified.value.toFixed(2),
      currency: verified.currency,
      source: "sales",
      isEstimate: false,
    };
  }

  const base = getRawPrice(card); // raw base ≈ Near Mint reference price
  const price = +(base.value * conditionMultiplier(cond)).toFixed(2);
  // `base.currency` is already null when there is no price, so a zero never
  // carries a currency it cannot back up.
  return {
    price,
    currency: price > 0 ? base.currency : null,
    source: "estimate",
    isEstimate: true,
  };
};

/**
 * Is there ANY price we can put on this card at this grade — provider feed or
 * Cardmarket estimate?
 *
 * Exactly `getConditionPrice(...).price > 0`, stated once so predicate and
 * getter cannot drift. `hasRawPrice` above keeps its narrower, Cardmarket-only
 * meaning; every "is this card priced" question that decides what a total or a
 * tile shows asks THIS one instead. The moment a conditionPrices-only card
 * exists the two answers differ, and #185 is what happens when a screen mixes
 * them.
 */
export const hasAnyPrice = (card, condition) =>
  getConditionPrice(card, condition).price > 0;

/**
 * Market-adjusted price for a card at a given condition (number only).
 * Prefers verified per-condition sales; otherwise a multiplier estimate.
 */
export const getAdjustedPrice = (card, condition) =>
  getConditionPrice(card, condition).price;

/**
 * Value for one side of a trade row (UserCard / collection entry), TAGGED.
 *
 * Uses listing askingPrice when set; otherwise market price × condition. The
 * askingPrice branch is SOURCE_CURRENCY by construction — ListCardModal labels
 * that input "(EUR)" and stores the number untagged — while the market branch
 * carries whatever the price it found is quoted in.
 *
 * @param {string} [conditionOverride] — e.g. live UI condition while building an offer
 * @returns {{value: number, currency: string|null}}
 */
export const getTradeSideValue = (entry, conditionOverride) => {
  if (!entry) return { value: 0, currency: null };
  // Only a For Sale row's price is a price (code review CARDS-TRADES-11): a
  // stale askingPrice left on a Trade Binder or Vault row must not value it.
  // Rows without a collectionType (older shapes) keep the old reading.
  const ap = entry.collectionType && entry.collectionType !== "sale" ? null : entry.askingPrice;
  if (typeof ap === "number" && !Number.isNaN(ap) && ap > 0) {
    return { value: +ap.toFixed(2), currency: SOURCE_CURRENCY };
  }
  const raw = entry.cardId;
  const card =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  const condition = conditionOverride || entry.condition || DEFAULT_CONDITION;
  const { price, currency } = getConditionPrice(card || entry, condition);
  return { value: price, currency };
};

/**
 * Value for a persisted trade subdoc (uses priceSnapshot when present).
 *
 * A snapshot is the record of an agreement, so its currency is read from the
 * record too. An untagged snapshot is EUR by history and not by assumption: the
 * guard this change replaced made a USD snapshot impossible to produce.
 * @returns {{value: number, currency: string|null}}
 */
export const getTradeCardSnapshotValue = (c) => {
  if (!c) return { value: 0, currency: null };
  const snap = c.priceSnapshot;
  if (typeof snap === "number" && !Number.isNaN(snap) && snap >= 0) {
    return {
      value: +snap.toFixed(2),
      currency: String(c.priceSnapshotCurrency || SOURCE_CURRENCY).toUpperCase(),
    };
  }
  const card = c.cardId && typeof c.cardId === "object" ? c.cardId : null;
  const { price, currency } = getConditionPrice(
    card || {},
    c.condition || DEFAULT_CONDITION
  );
  return { value: price, currency };
};

// ── totals ───────────────────────────────────────────────────────────────────

/**
 * Fold a list of TAGGED amounts into one subtotal per currency.
 *
 * This is the only place in the app that adds two card values together, and it
 * can only ever add two that carry the same code — which is what makes "a
 * fairness total never mixes currencies" a property of the code rather than a
 * promise in a comment. It is the same discipline `trades/cashOffer.js`
 * already applies to a cash term, generalised from cash to card values.
 *
 * `currencies` is ordered by how many cards contributed, alphabetical on a tie,
 * so the majority currency leads the line a header prints.
 *
 * @param {Array<{value: number, currency: string|null}>} amounts
 * @returns {{byCurrency: Object<string, number>, currencies: string[],
 *            counts: Object<string, number>, mixed: boolean}}
 */
export const partitionByCurrency = (amounts) => {
  const byCurrency = {};
  const counts = {};

  (amounts || []).forEach((amount) => {
    const value = Number(amount?.value);
    const currency = amount?.currency;
    // An untagged or non-positive figure is not money we can add. Skipping it
    // here is what keeps a null currency from becoming a silent euro.
    if (!currency || !Number.isFinite(value) || value <= 0) return;
    byCurrency[currency] = +((byCurrency[currency] || 0) + value).toFixed(2);
    counts[currency] = (counts[currency] || 0) + 1;
  });

  const currencies = Object.keys(byCurrency).sort(
    (a, b) => counts[b] - counts[a] || a.localeCompare(b)
  );

  return { byCurrency, currencies, counts, mixed: currencies.length > 1 };
};

/**
 * Estimated value of a LIST of cards — the number a page header prints above a
 * grid of tiles.
 *
 * This exists because the Collection and Wishlist headers each summed
 * `card.cardmarket.prices.avg30` by hand. That did three wrong things at once:
 * it read a raw field the adapter does not prefer (so the header contradicted
 * the very tiles beneath it), it skipped the condition multiplier (so a Damaged
 * copy was totalled at Near Mint), and `|| 0` quietly counted a card we have no
 * price for as worth nothing — presenting a partial figure as a complete one.
 *
 * So: one implementation, going through the same getAdjustedPrice the tiles use,
 * and unpriced cards are COUNTED rather than added as zero. The caller decides
 * how to say "…and 3 more we can't price", but it can no longer fail to know.
 *
 * @param {Array<{card: object, condition?: string}>} rows
 * @returns {{ byCurrency: Object<string, number>, currencies: string[],
 *             currency: string|null, total: number|null, priced: number,
 *             unpriced: number, hasUnknown: boolean, mixed: boolean }}
 *
 * Mixed currencies do not produce a total. `total` is null then, on purpose: a
 * caller that renders it prints "" (formatMoney returns "" for null) rather
 * than a wrong number, and the shape change is loud rather than silent. In the
 * single-currency case `total` and `currency` are exactly what they always
 * were, so nothing that predates the dollar has to move.
 */
export const summariseEstimatedValue = (rows) => {
  let priced = 0;
  let unpriced = 0;
  const amounts = [];

  (rows || []).forEach((row) => {
    const card = row?.card;
    // The `> 0` gate is what makes a stored 0 read as "unknown" rather than
    // "free". It reads the same getter the tiles do, so the header can no
    // longer disagree with what is printed beneath it.
    const { price, currency } = getConditionPrice(card, row?.condition);
    if (!(price > 0) || !currency) {
      unpriced += 1;
      return;
    }
    amounts.push({ value: price, currency });
    priced += 1;
  });

  const { byCurrency, currencies, mixed } = partitionByCurrency(amounts);

  return {
    byCurrency,
    currencies,
    currency: mixed ? null : currencies[0] || null,
    total: mixed ? null : +(byCurrency[currencies[0]] || 0).toFixed(2),
    priced,
    unpriced,
    hasUnknown: unpriced > 0,
    mixed,
  };
};

// ── fair-trade helpers ───────────────────────────────────────────────────────

/**
 * Returns a 0-100 value indicating where the needle sits on the fair-trade bar.
 * 50 = perfectly equal. < 50 = you're underpaying, > 50 = you're overpaying.
 */
export const getFairTradePercent = (yourValue, theirValue) => {
  const y = Math.max(0, Number(yourValue) || 0);
  const t = Math.max(0, Number(theirValue) || 0);
  const total = y + t;
  if (total === 0) return 50;
  // Share of combined value you contribute: 50% = both sides equal in $
  return Math.max(2, Math.min(98, (y / total) * 100));
};

export const getValueDifference = (yourValue, theirValue) => {
  const diff = yourValue - theirValue;
  const avg  = (yourValue + theirValue) / 2;
  const pct  = avg > 0 ? Math.abs(diff / avg) * 100 : 0;
  return { diff, pct };
};

/**
 * Difference between a value and a REFERENCE value, as a share of the
 * reference. Use this wherever one of the two numbers is the yardstick the
 * other is judged against — a listing's asking price against the market
 * estimate, say — and getValueDifference above wherever neither is, such as
 * two collectors' sides in a trade.
 *
 * The denominators are what differ: this one divides by marketValue, the
 * symmetric one divides by the mean of both. Because |y - t| <= y + t for
 * non-negative inputs, the mean-relative reading can never exceed 200%, which
 * squeezes every overask — 2x the reference reads 66.7%, 1000x reads 199.6% —
 * into a narrow band under that ceiling. This one is unbounded above.
 */
export const getMarketValueDifference = (yourValue, marketValue) => {
  const diff = yourValue - marketValue;
  const pct  = marketValue > 0 ? Math.abs(diff / marketValue) * 100 : 0;
  return { diff, pct };
};
